/**
 * Artwork API client — typed wrappers around the customer artwork
 * endpoints used by the storefront's FileUploader, Artwork Library, and
 * cart/order flows.
 *
 * Per `.claude/rules/frontend-data-flow.md`, this module is the sole
 * gateway between the platform's `/api/v1/account/artwork`,
 * `/api/v1/artwork/*`, and `/api/v1/orders/:id/artwork` endpoints and
 * the rest of the web stack — the artworkSlice's thunks call these
 * helpers, never axios directly.
 *
 * Functions are 1:1 with the slice's createAsyncThunk actions:
 *
 *  - {@link fetchArtworkLibrary}     GET    /account/artwork
 *  - {@link deleteArtworkFile}       DELETE /account/artwork/:id
 *  - {@link uploadArtworkFile}       passthrough — the multipart S3 flow
 *                                    is orchestrated by `artworkUploader.ts`,
 *                                    which already returns the completed
 *                                    {@link ArtworkFile}; this helper exists
 *                                    so the slice has a typed seam for the
 *                                    "upload completed" event without
 *                                    re-orchestrating the multipart steps.
 *  - {@link fetchFileValidationStatus} GET  /artwork/:id/status
 *  - {@link attachFileToOrderItem}   POST   /orders/:orderId/artwork
 *
 * Every function uses the shared {@link apiClient} and returns the
 * unwrapped DTO via {@link unwrap} / {@link throwAsApiError} so error
 * paths surface as {@link ApiError} (the canonical type carrying the
 * platform's envelope fields — status, severity, message, errors).
 *
 * SH-FILES-003.
 */
import type { AxiosResponse } from 'axios';

import { apiClient } from '@dfq/shared/api/client';
import {
  throwAsApiError,
  unwrap,
} from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  ArtworkFile,
  ValidationStatus,
} from '@dfq/shared/types/artwork';

// Re-export the shared types so callers can `import { ArtworkFile, ... }
// from '@dfq/shared/api/artwork'` without reaching into the types
// module directly. Keeps the public API surface concentrated.
export type {
  ArtworkFile,
  ArtworkFileVersion,
  ValidationCheck,
  ValidationResult,
  ValidationStatus,
  UploadQueueEntry,
} from '@dfq/shared/types/artwork';

// ----------------------------------------------------------------------------
// Wire-shape envelopes
// ----------------------------------------------------------------------------

interface ArtworkLibraryResponse {
  files: ArtworkFile[];
}

/**
 * Response shape for the `attach to order item` endpoint. The platform
 * confirms the pivot row that was created.
 */
export interface AttachFileToOrderItemResponse {
  order_item_id: number;
  artwork_file_id: number;
  is_primary: boolean;
}

/**
 * Subset of {@link ArtworkFile} returned by the polling endpoint. The
 * server only sends the validation-relevant fields here (id, status,
 * results) — the rest of the record is unchanged from the upload.
 */
export type ArtworkFileValidationStatus = Pick<
  ArtworkFile,
  'id' | 'validation_status' | 'validation_results_json'
>;

/**
 * Options accepted by {@link fetchFileValidationStatus}. The signal is
 * forwarded to axios so an aborted thunk's polling loop tears down the
 * in-flight HTTP request as well as cancelling the next setTimeout.
 */
export interface FetchValidationStatusOptions {
  signal?: AbortSignal;
}

/**
 * Argument shape for {@link uploadArtworkFile}. The multipart flow
 * (initiate / put parts / complete) is owned by `artworkUploader.ts`;
 * by the time the slice calls this helper, the server has already
 * registered the {@link ArtworkFile} record. This helper is therefore
 * a passthrough seam — it gives the slice a single thunk-target it can
 * dispatch on completion without the slice having to know about the
 * multipart machinery.
 *
 * `uploadId` is the client-side queue id that the slice uses to find
 * the matching `UploadQueueEntry` and mark it `complete` when this
 * resolves.
 */
export interface UploadArtworkFileArg {
  /** Client-generated queue id from {@link UploadQueueEntry.id}. */
  uploadId: string;
  /** Server-confirmed ArtworkFile record from the multipart complete step. */
  file: ArtworkFile;
}

/**
 * Return shape for {@link uploadArtworkFile}. Carries both the queue
 * id (so the slice can correlate to the right `UploadQueueEntry`) and
 * the `ArtworkFile` record (so the slice can merge it into `files`).
 */
export interface UploadArtworkFileResult {
  uploadId: string;
  file: ArtworkFile;
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

/**
 * Run a single axios call against the artwork endpoints, normalising
 * any error to {@link ApiError} via {@link throwAsApiError} and
 * unwrapping the platform envelope on success.
 */
async function callArtworkEndpoint<T>(
  fn: () => Promise<AxiosResponse<ApiEnvelope<T>>>,
): Promise<T> {
  let response: AxiosResponse<ApiEnvelope<T>>;
  try {
    response = await fn();
  } catch (err) {
    throwAsApiError(err);
  }
  return unwrap<T>(response.data, response.status);
}

// ----------------------------------------------------------------------------
// Public API — one function per slice thunk
// ----------------------------------------------------------------------------

/**
 * `GET /api/v1/account/artwork` — fetch the authenticated customer's
 * persisted artwork library. Returns the array of {@link ArtworkFile}
 * records, sorted by `uploaded_at` descending (server-side ordering).
 */
export async function fetchArtworkLibrary(): Promise<ArtworkFile[]> {
  const data = await callArtworkEndpoint<ArtworkLibraryResponse>(() =>
    apiClient.get<ApiEnvelope<ArtworkLibraryResponse>>('/account/artwork'),
  );
  return data.files;
}

/**
 * `DELETE /api/v1/account/artwork/:id` — permanently delete an artwork
 * file record (and its associated S3 object). Resolves on 204; throws
 * {@link ApiError} on 404 / 422 / 5xx.
 */
export async function deleteArtworkFile(id: number): Promise<void> {
  await callArtworkEndpoint<Record<string, never>>(() =>
    apiClient.delete<ApiEnvelope<Record<string, never>>>(
      `/account/artwork/${encodeURIComponent(String(id))}`,
    ),
  );
}

/**
 * Passthrough seam between the multipart upload orchestrator
 * (`artworkUploader.ts`) and the slice's `uploadArtworkFile` thunk.
 * The orchestrator has already hit `POST /api/v1/artwork/upload` and
 * received the server-confirmed {@link ArtworkFile}; this helper
 * simply pairs that record back with the queue id so the slice can
 * find the matching {@link UploadQueueEntry} when the thunk
 * fulfills.
 *
 * Resolves synchronously (no additional network call). Exposed as a
 * function returning a Promise so the slice's thunk pattern stays
 * uniform with the other four helpers.
 */
export async function uploadArtworkFile(
  arg: UploadArtworkFileArg,
): Promise<UploadArtworkFileResult> {
  return { uploadId: arg.uploadId, file: arg.file };
}

/**
 * `GET /api/v1/artwork/:id/status` — poll a file's validation state.
 * Used by the slice's polling thunk; the polling loop (interval +
 * max-attempts) lives in the thunk, not here.
 *
 * The signal is forwarded to axios so cancelling the parent thunk
 * tears down the in-flight HTTP request.
 */
export async function fetchFileValidationStatus(
  id: number,
  options: FetchValidationStatusOptions = {},
): Promise<ArtworkFileValidationStatus> {
  return callArtworkEndpoint<ArtworkFileValidationStatus>(() =>
    apiClient.get<ApiEnvelope<ArtworkFileValidationStatus>>(
      `/artwork/${encodeURIComponent(String(id))}/status`,
      { signal: options.signal },
    ),
  );
}

/**
 * `POST /api/v1/orders/:orderId/artwork` — attach an uploaded artwork
 * file to a specific order item. Returns the pivot row the platform
 * persisted (so the caller has the canonical `is_primary` value the
 * server resolved).
 */
export async function attachFileToOrderItem(
  orderId: number,
  artworkFileId: number,
  isPrimary?: boolean,
): Promise<AttachFileToOrderItemResponse> {
  const body: {
    artwork_file_id: number;
    is_primary?: boolean;
  } = { artwork_file_id: artworkFileId };
  if (isPrimary !== undefined) {
    body.is_primary = isPrimary;
  }
  return callArtworkEndpoint<AttachFileToOrderItemResponse>(() =>
    apiClient.post<ApiEnvelope<AttachFileToOrderItemResponse>>(
      `/orders/${encodeURIComponent(String(orderId))}/artwork`,
      body,
    ),
  );
}

/**
 * Convenience grouping export — `import { artworkApi } from
 * '@dfq/shared/api/artwork'` if the namespace style is preferred.
 */
export const artworkApi = {
  fetchArtworkLibrary,
  deleteArtworkFile,
  uploadArtworkFile,
  fetchFileValidationStatus,
  attachFileToOrderItem,
};

// Re-export the polling-status type at the top level so callers don't
// need to know the field-pick origin.
export type ValidationStatusValue = ValidationStatus;
